import {
  useCallback, useEffect, useRef, useState, useSyncExternalStore,
} from 'react';
import { createMyIpDraft } from '$/domains/myIp/myIpService';
import useCreateRoom from '$/hooks/useCreateRoom';
import { validateMyIpRoomId } from '$/domains/myIp/agentBinding';
import { rememberMyIpCreationHandoff, type MyIpCreationHandoff } from '$/domains/myIp/creationHandoff';
import { captureMyIpCanvasContext } from '$/pages/RoboneoTeamFlow/utils/myIpCanvasContext';
import { getLoadedTeamFlowIdentity, subscribeLoadedTeamFlowIdentity } from '$/pages/RoboneoTeamFlow/utils/teamFlowLoadedIdentity';
import { getRegisteredTeamFlowContext } from '$/pages/RoboneoTeamFlow/utils/teamFlowContextRegistry';
import { trackMyIpCreateEntranceClick, type MyIpCreateEntranceLocation } from '$/domains/myIp/tracking';
import { navigate } from '@/common/agents';
import { getMyIpCreationHref } from '@/utils/myIp/routes';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import { useMyIpCreationOrigin } from '@/components/MyIpCreationOriginProvider';

/** 服务端渲染没有已加载画布，普通入口仍由会话就绪状态控制。 */
const getServerIdentity = () => undefined;

export interface MyIpCreationEntry {
  ipId: string;
  handoff: MyIpCreationHandoff;
  isCurrent: () => boolean;
}

interface MyIpCreateOptions {
  trackingLocation?: MyIpCreateEntranceLocation;
}

/** 通过统一入口创建 IP 专属会话与草稿；保留来源、失效校验及失败重试目标。 */
export function useMyIpCreateAction({ requireCanvas = false, onOpen, trackingLocation }: {
  requireCanvas?: boolean;
  onOpen?: (entry: MyIpCreationEntry) => void;
  trackingLocation?: MyIpCreateEntranceLocation;
} = {}) {
  const session = useMyIpSession();
  const { createRoom } = useCreateRoom({});
  const providedOrigin = useMyIpCreationOrigin();
  const defaultOrigin = providedOrigin === undefined ? { kind: 'new-project' as const } : providedOrigin;
  const origin = requireCanvas && providedOrigin?.kind !== 'canvas' ? null : defaultOrigin;
  const loadedIdentity = useSyncExternalStore(subscribeLoadedTeamFlowIdentity, getLoadedTeamFlowIdentity, getServerIdentity);
  const canvasVersion = useRef({ identity: loadedIdentity, version: 0 });
  if (canvasVersion.current.identity !== loadedIdentity) {
    canvasVersion.current = { identity: loadedIdentity, version: canvasVersion.current.version + 1 };
  }
  const originReady = Boolean(origin && (origin.kind === 'new-project' || (loadedIdentity
    && loadedIdentity.projectId === origin.projectId && loadedIdentity.roomId === origin.roomId
    && loadedIdentity.canvasId === origin.canvasId && loadedIdentity.scopeKey === session.scopeKey)));
  const enabled = session.enabled && originReady;
  const contextKey = JSON.stringify([
    session.scopeKey, session.paths.prefix, session.enabled, origin,
    origin?.kind === 'canvas' ? canvasVersion.current.version : undefined,
  ]);
  const context = useRef({
    key: contextKey,
    roomId: '',
    ipId: '',
    handoff: undefined as MyIpCreationHandoff | undefined,
    isOriginCurrent: undefined as (() => boolean) | undefined,
    pending: null as Promise<void> | null,
  });
  const mounted = useRef(false);
  const [pending, setPending] = useState(false);
  const [, refreshContext] = useState(0);
  if (context.current.key !== contextKey) { context.current = {
    key: contextKey, roomId: '', ipId: '', handoff: undefined as MyIpCreationHandoff | undefined, isOriginCurrent: undefined, pending: null,
  }; }
  const { current } = context;

  /** 卸载后不再更新 UI 或执行导航。 */
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  /** 保存完成后释放本次创建目标，房间内下一次创建使用新的独立草稿。 */
  const reset = useCallback(() => {
    context.current = {
      key: context.current.key, roomId: '', ipId: '', handoff: undefined as MyIpCreationHandoff | undefined, isOriginCurrent: undefined, pending: null,
    };
    setPending(false);
    refreshContext((revision) => revision + 1);
  }, []);

  /** 并发调用复用 Promise；取得真实 IP 后由宿主打开弹窗，普通入口仍导航到创建页。 */
  const create = ({ trackingLocation: actionTrackingLocation }: MyIpCreateOptions = {}): Promise<void> => {
    if (!enabled || !origin || !mounted.current || context.current !== current) return Promise.reject(new Error('IP creation context expired'));
    if (current.pending) return current.pending;
    if (origin.kind === 'canvas' && !current.isOriginCurrent) current.isOriginCurrent = captureMyIpCanvasContext();
    let isReadonly: (() => boolean) | undefined;
    /** 统一入口和后续草稿流程共用来源校验，临时只读不作废已创建的会话。 */
    const isCurrent = () => mounted.current && context.current === current
      && (!current.isOriginCurrent || current.isOriginCurrent());
    /** 原画布被替换、切账号或卸载后不能把旧请求结果导航到新上下文。 */
    const assertCurrent = () => {
      if (!isCurrent()) {
        throw new Error('IP creation context expired');
      }
    };
    /** 临时只读不废弃已成功创建的房间/IP，恢复编辑权限后仍可继续同一次动作。 */
    const assertWritable = () => {
      assertCurrent();
      if (origin.kind === 'canvas' && (isReadonly?.() || getRegisteredTeamFlowContext()?.playground.config.readonly)) {
        throw new Error('IP creation canvas is readonly');
      }
    };
    try { assertWritable(); } catch (error) { return Promise.reject(error); }
    setPending(true);
    current.pending = (async () => {
      if (origin.kind === 'canvas') {
        // 剪辑器只读状态依赖 Flowgram，仅在真实画布创建时加载，不进入首页初始依赖。
        const { isTeamFlowCanvasReadonly } = await import('$/pages/RoboneoTeamFlow/utils/isTeamFlowCanvasReadonly');
        isReadonly = isTeamFlowCanvasReadonly;
        assertWritable();
      }
      if (!current.roomId) {
        // 仅 IP 创建指定执行 Agent 和角色摘要类型，不修改普通房间的默认配置。
        const room = await createRoom({
          roomOnly: true,
          trackLocation: 'ip_creation',
          isCurrent,
          roomCreateParams: {
            agent: 'ip_creation_executor',
            extra: { feature: { summary_type: 'ip_character' } },
          },
        });
        assertCurrent();
        if (!room) throw new Error('IP Agent room creation failed');
        current.roomId = validateMyIpRoomId(room.roomId);
      }
      assertWritable();
      if (!current.ipId) {
        current.ipId = (await createMyIpDraft({ sourceSessionId: current.roomId })).ipId;
        const location = actionTrackingLocation ?? trackingLocation;
        if (location) {
          trackMyIpCreateEntranceClick({
            roomId: current.roomId,
            projectId: origin.kind === 'canvas' ? origin.projectId : undefined,
            ipId: current.ipId,
          }, location);
        }
      }
      assertWritable();
      if (!current.handoff) { current.handoff = rememberMyIpCreationHandoff({
        scopeKey: session.scopeKey,
        prefix: session.paths.prefix,
        ipId: current.ipId,
        sourceSessionId: current.roomId,
        origin,
      }); }
      if (onOpen) {
        onOpen({
          ipId: current.ipId,
          handoff: current.handoff,
          isCurrent,
        });
      } else {
        // router 模式使用 Next 内部路由，避免整页刷新；预编码参数避免导航适配器直接拼接 query 导致房间 ID 失真。
        navigate({
          mode: 'router',
          pathname: getMyIpCreationHref('/--roboneo', current.ipId, current.roomId, origin.kind === 'canvas' ? {
            projectId: origin.projectId,
            canvasId: origin.canvasId,
          } : undefined),
        });
      }
    })().finally(() => {
      current.pending = null;
      if (mounted.current && context.current === current) setPending(false);
    });
    return current.pending;
  };

  return {
    create, reset, pending: Boolean(current.pending) && pending, enabled,
  };
}
